"use client";

import Image from "@/lib/compat/image";
import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

type BankKey = "FAB" | "Itau";

const BANK_META: Record<BankKey, { legalName: string; jurisdiction: string; logo: string }> = {
  FAB:  { legalName: "First Abu Dhabi Bank", jurisdiction: "UAE",    logo: "/banks/fab.svg" },
  Itau: { legalName: "Itaú Unibanco S.A.",   jurisdiction: "Brazil", logo: "/banks/itau.svg" },
};

/**
 * BankBadge renders a financing partner with logo + full legal name +
 * jurisdiction + funding-share annotation. Per Decision 18: every bank
 * reference in audience-facing UI uses this component; text-only bank
 * names are a build error.
 *
 * The 8pt legal footer accompanies any screen rendering a BankBadge — see
 * frontend/public/banks/legal-note.txt for the canonical copy.
 */
export function BankBadge({
  name,
  fundingPct,
  variant = "tile",
  className,
}: {
  name: BankKey;
  fundingPct?: number;
  variant?: "tile" | "edge" | "compact";
  className?: string;
}) {
  const t = useTranslations("bankBadge");
  const meta = BANK_META[name];
  if (!meta) return null;

  if (variant === "edge") {
    return (
      <span className={clsx("inline-flex items-center gap-1.5 rounded-full border border-state-financed/40 bg-orbitra-ink/80 px-2 py-1 text-[10px] font-semibold text-state-financed", className)}>
        <Image src={meta.logo} alt={meta.legalName} width={14} height={14} />
        {fundingPct !== undefined ? `${fundingPct}% ${name}` : name}
      </span>
    );
  }
  if (variant === "compact") {
    return (
      <span className={clsx("inline-flex items-center gap-2 text-[11px] text-state-financed", className)}>
        <Image src={meta.logo} alt={meta.legalName} width={16} height={16} />
        <span>{name}</span>
      </span>
    );
  }
  return (
    <div className={clsx("glass flex items-center gap-3 px-4 py-3", className)}>
      <Image src={meta.logo} alt={meta.legalName} width={32} height={32} />
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-state-financed">{meta.legalName}</span>
        <span className="text-[10px] uppercase tracking-wider text-orbitra-muted">{meta.jurisdiction}</span>
        {fundingPct !== undefined && (
          <span className="mt-1 text-[11px] font-semibold text-state-financed">
            {fundingPct}% {t("partialFinancingSuffix")}
          </span>
        )}
      </div>
    </div>
  );
}
